/**
 * MD-14 FE-LANDINGS — consent banner state machine + persistence
 *
 * Granular 3-type consent per LEGAL §9:
 *   essential       — auto-on, locked, required
 *   analytics       — opt-in (CF Web Analytics anonymized session)
 *   personalization — opt-in (Pro tier only — deferred Phase 3+)
 *
 * Submit endpoint POST /api/v1/public/consent:
 *   - persist to D1 consent_log (3 enum rows: site_essential auto + 2 toggle outcomes)
 *   - return consent_id
 *   - set asklaw_consent cookie 1y TTL
 *
 * ESC dismiss = "essential only" (analytics+personalization=false) — defensive
 * default per F-FE-LANDINGS §5.
 */

export type ConsentChoice = {
  essential: true; // always true (locked)
  analytics: boolean;
  personalization: boolean;
};

export type ConsentSubmitInput = {
  choice: ConsentChoice;
  lang: 'vi' | 'en';
  source_landing?: string;
  ip_country?: string;
};

export type ConsentSubmitResult =
  | { ok: true; consent_id: string; recorded_at: string; cookie: string }
  | { ok: false; status: 400 | 500; reason: string };

function generateConsentId(): string {
  return `cs_${globalThis.crypto.randomUUID()}`;
}

export function buildConsentCookie(consent_id: string, secure = true): string {
  const attrs = [
    `asklaw_consent=${consent_id}`,
    'Path=/',
    'Max-Age=31536000',
    'SameSite=Lax',
    'HttpOnly',
  ];
  if (secure) attrs.push('Secure');
  return attrs.join('; ');
}

export interface ConsentDB {
  insertConsentRows(rows: ConsentRow[]): Promise<void>;
}

export type ConsentRow = {
  consent_id: string;
  consent_type:
    | 'site_essential'
    | 'site_analytics'
    | 'site_personalization';
  recorded_at: string;
  user_id: null;
  anon_id: null;
  ip_country: string | null;
  consent_text_version: string;
};

const CONSENT_TEXT_VERSION = 'v1.0';

export async function recordConsent(
  input: ConsentSubmitInput,
  db: ConsentDB,
  idGen: () => string = generateConsentId
): Promise<ConsentSubmitResult> {
  if (input.choice.essential !== true) {
    return { ok: false, status: 400, reason: 'essential consent must be true' };
  }
  const consent_id = idGen();
  const recorded_at = new Date().toISOString();
  const ip_country = input.ip_country ?? null;

  // Persist 3 rows: essential + (analytics if granted) + (personalization if granted).
  // Decline rows omitted Day-1; opt-in only model. consent_log is append-only.
  const rows: ConsentRow[] = [
    {
      consent_id,
      consent_type: 'site_essential',
      recorded_at,
      user_id: null,
      anon_id: null,
      ip_country,
      consent_text_version: CONSENT_TEXT_VERSION,
    },
  ];
  if (input.choice.analytics) {
    rows.push({
      consent_id,
      consent_type: 'site_analytics',
      recorded_at,
      user_id: null,
      anon_id: null,
      ip_country,
      consent_text_version: CONSENT_TEXT_VERSION,
    });
  }
  if (input.choice.personalization) {
    rows.push({
      consent_id,
      consent_type: 'site_personalization',
      recorded_at,
      user_id: null,
      anon_id: null,
      ip_country,
      consent_text_version: CONSENT_TEXT_VERSION,
    });
  }

  try {
    await db.insertConsentRows(rows);
  } catch (err) {
    return { ok: false, status: 500, reason: (err as Error).message };
  }

  return {
    ok: true,
    consent_id,
    recorded_at,
    cookie: buildConsentCookie(consent_id),
  };
}

/** Defensive default — ESC dismiss path. */
export function essentialOnlyChoice(): ConsentChoice {
  return { essential: true, analytics: false, personalization: false };
}
